import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { AuthAPI } from '@api/auth';
import {
  getDefaultPathByRole,
  normalizeRoleCode,
  persistNormalizedStoredUserInfo,
  readStoredUserInfo,
  saveStoredUserInfo,
  type AppRoleCode,
} from '@/utils/auth';

const EMPTY_ALLOWED_ROLES: AppRoleCode[] = [];

interface RouteGuardProps {
  children: ReactNode;
  requireAuth?: boolean;
  allowedRoles?: AppRoleCode[];
}

interface GuardState {
  loading: boolean;
  isAuthenticated: boolean;
  roleCode: AppRoleCode | null;
}

function GuardLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <i className="ri-loader-4-line animate-spin text-3xl text-primary-600"></i>
    </div>
  );
}

export default function RouteGuard({
  children,
  requireAuth = false,
  allowedRoles,
}: RouteGuardProps) {
  const location = useLocation();
  const resolvedAllowedRoles = allowedRoles ?? EMPTY_ALLOWED_ROLES;
  const [guardState, setGuardState] = useState<GuardState>({
    loading: requireAuth || resolvedAllowedRoles.length > 0,
    isAuthenticated: false,
    roleCode: null,
  });

  const shouldCheckPermission = requireAuth || resolvedAllowedRoles.length > 0;
  const redirectSource = useMemo(
    () => `${location.pathname}${location.search}${location.hash}`,
    [location.hash, location.pathname, location.search],
  );

  useEffect(() => {
    let isUnmounted = false;

    if (!shouldCheckPermission) {
      setGuardState({
        loading: false,
        isAuthenticated: false,
        roleCode: null,
      });
      return () => {
        isUnmounted = true;
      };
    }

    const resolvePermission = async () => {
      const accessToken = sessionStorage.getItem('access_token');
      if (!accessToken) {
        if (!isUnmounted) {
          setGuardState({
            loading: false,
            isAuthenticated: false,
            roleCode: null,
          });
        }
        return;
      }

      const storedUserInfo = persistNormalizedStoredUserInfo(
        readStoredUserInfo(),
        accessToken,
      );
      const storedRoleCode = normalizeRoleCode(
        storedUserInfo?.roleCode || storedUserInfo?.currentRole?.code || storedUserInfo?.role,
      );

      if (storedUserInfo && storedRoleCode) {
        if (!isUnmounted) {
          setGuardState({
            loading: false,
            isAuthenticated: true,
            roleCode: storedRoleCode,
          });
        }
        return;
      }

      try {
        const response = await AuthAPI.getUserInfo();
        if (response.statusCode?.value === 200 && response.data) {
          const normalizedUserInfo = saveStoredUserInfo(response.data, accessToken);
          const resolvedRoleCode = normalizeRoleCode(
            normalizedUserInfo.roleCode || normalizedUserInfo.currentRole?.code || normalizedUserInfo.role,
          );

          if (!isUnmounted) {
            setGuardState({
              loading: false,
              isAuthenticated: true,
              roleCode: resolvedRoleCode,
            });
          }
          return;
        }
      } catch (error) {
        console.error('路由守卫获取用户信息失败:', error);
      }

      if (!isUnmounted) {
        setGuardState({
          loading: false,
          isAuthenticated: false,
          roleCode: null,
        });
      }
    };

    void resolvePermission();

    return () => {
      isUnmounted = true;
    };
  }, [resolvedAllowedRoles, shouldCheckPermission, redirectSource]);

  if (!shouldCheckPermission) {
    return <>{children}</>;
  }

  if (guardState.loading) {
    return <GuardLoading />;
  }

  if (!guardState.isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: redirectSource }}
      />
    );
  }

  if (resolvedAllowedRoles.length > 0) {
    const isAllowed = !!guardState.roleCode && resolvedAllowedRoles.includes(guardState.roleCode);
    if (!isAllowed) {
      return (
        <Navigate
          to={getDefaultPathByRole(guardState.roleCode)}
          replace
        />
      );
    }
  }

  return <>{children}</>;
}

